import React from 'react';
import { ContentData } from '../data/contentData';

interface AboutPageProps {
  content: ContentData;
}

const AboutPage: React.FC<AboutPageProps> = ({ content }) => {
  const stats = [
    { number: '৫০+', label: 'বছরের অভিজ্ঞতা', icon: '📚' },
    { number: '২০০০+', label: 'সফল শিক্ষার্থী', icon: '🎓' },
    { number: '৫০+', label: 'অভিজ্ঞ শিক্ষক', icon: '👨‍🏫' },
    { number: '১০০%', label: 'পাসের হার', icon: '🏆' }
  ];

  const achievements = [
    {
      year: '১৯৭৪',
      title: 'প্রতিষ্ঠা',
      description: 'রাজউক কলেজ প্রতিষ্ঠিত হয় শিক্ষার আলো ছড়ানোর লক্ষ্যে'
    },
    {
      year: '১৯৮৫',
      title: 'এমপিও স্বীকৃতি',
      description: 'শিক্ষা মন্ত্রণালয় থেকে এমপিও (মানবসম্পদ উন্নয়ন) স্বীকৃতি লাভ'
    },
    {
      year: '২০০০',
      title: 'প্রযুক্তি সংযোজন',
      description: 'কম্পিউটার ল্যাব ও আধুনিক প্রযুক্তি সংযোজন'
    },
    {
      year: '২০১০',
      title: 'সায়েন্স ল্যাব',
      description: 'আধুনিক বিজ্ঞান গবেষণাগার স্থাপন'
    },
    {
      year: '২০২০',
      title: 'ডিজিটালাইজেশন',
      description: 'অনলাইন শিক্ষা ব্যবস্থা চালু'
    },
    {
      year: '২০২৫',
      title: 'নতুন অধ্যায়',
      description: 'আধুনিক শিক্ষা পদ্ধতি ও প্রযুক্তির সমন্বয়'
    }
  ];

  const facilities = [
    {
      title: 'আধুনিক শ্রেণিকক্ষ',
      description: 'সব শ্রেণির জন্য আলো-বাতাসযুক্ত আধুনিক শ্রেণিকক্ষ',
      icon: '🏫'
    },
    {
      title: 'বিজ্ঞান গবেষণাগার',
      description: 'পদার্থবিজ্ঞান, রসায়ন ও জীববিজ্ঞানের জন্য আধুনিক ল্যাব',
      icon: '🔬'
    },
    {
      title: 'কম্পিউটার ল্যাব',
      description: 'আধুনিক কম্পিউটার ও ইন্টারনেট সুবিধা',
      icon: '💻'
    },
    {
      title: 'গ্রন্থাগার',
      description: 'বিশাল গ্রন্থাগার ও পড়ার কক্ষ',
      icon: '📚'
    },
    {
      title: 'ক্রীড়া মাঠ',
      description: 'বিভিন্ন খেলার জন্য বিশাল মাঠ',
      icon: '⚽'
    },
    {
      title: 'সাংস্কৃতিক মঞ্চ',
      description: 'সাংস্কৃতিক অনুষ্ঠানের জন্য মঞ্চ',
      icon: '🎭'
    }
  ];

  const leadership = [
    {
      name: 'প্রফেসর ড. মো. আবুল কালাম',
      position: 'অধ্যক্ষ',
      experience: '২৫+ বছর',
      education: 'পিএইচডি, ঢাকা বিশ্ববিদ্যালয়',
      image: '👨‍💼'
    },
    {
      name: 'মো. রফিকুল ইসলাম',
      position: 'উপাধ্যক্ষ',
      experience: '২০+ বছর',
      education: 'এম.এসসি, রাজশাহী বিশ্ববিদ্যালয়',
      image: '👨‍🏫'
    },
    {
      name: 'ড. ফাতেমা খাতুন',
      position: 'শিক্ষা উপদেষ্টা',
      experience: '১৫+ বছর',
      education: 'পিএইচডি, জাহাঙ্গীরনগর বিশ্ববিদ্যালয়',
      image: '👩‍🏫'
    }
  ];

  return (
    <div className="py-16 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4 bangla-font">{content.about.title}</h1>
          <p className="text-lg text-gray-600 bangla-font">শিক্ষা, সংস্কৃতি ও নৈতিকতার কেন্দ্র</p>
        </div>

        {/* Introduction */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6 bangla-font">রাজউক কলেজের পরিচিতি</h2>
              <p className="text-gray-700 leading-relaxed mb-6 bangla-font">
                রাজউক কলেজ ১৯৭৪ সালে প্রতিষ্ঠিত একটি ঐতিহ্যবাহী শিক্ষা প্রতিষ্ঠান। 
                পাঁচ দশকেরও বেশি সময় ধরে আমরা মানসম্পন্ন শিক্ষা প্রদান করে আসছি। 
                আমাদের লক্ষ্য শিক্ষার্থীদেরকে শুধু ভালো ফলাফলই নয়, বরং সুনাগরিক হিসেবে গড়ে তোলা।
              </p>
              <p className="text-gray-700 leading-relaxed bangla-font">
                আমরা বিশ্বাস করি যে শিক্ষা হল জীবনের সবচেয়ে গুরুত্বপূর্ণ হাতিয়ার। 
                তাই আমরা আমাদের শিক্ষার্থীদেরকে আধুনিক শিক্ষা পদ্ধতি, 
                সাংস্কৃতিক মূল্যবোধ এবং নৈতিক শিক্ষার সমন্বয়ে গড়ে তুলি।
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-8 text-white">
              <h3 className="text-2xl font-bold mb-4 bangla-font">আমাদের প্রতিশ্রুতি</h3>
              <ul className="space-y-3 bangla-font">
                <li className="flex items-center">
                  <span className="text-2xl mr-3">✅</span>
                  মানসম্পন্ন শিক্ষা প্রদান
                </li>
                <li className="flex items-center">
                  <span className="text-2xl mr-3">✅</span>
                  সাংস্কৃতিক মূল্যবোধ সংরক্ষণ
                </li>
                <li className="flex items-center">
                  <span className="text-2xl mr-3">✅</span>
                  আধুনিক প্রযুক্তির ব্যবহার
                </li>
                <li className="flex items-center">
                  <span className="text-2xl mr-3">✅</span>
                  নৈতিক শিক্ষার গুরুত্ব
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🎯</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 bangla-font">{content.about.mission}</h2>
            </div>
            <p className="text-gray-700 leading-relaxed text-center bangla-font">
              {content.about.missionText}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">👁️</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 bangla-font">{content.about.vision}</h2>
            </div>
            <p className="text-gray-700 leading-relaxed text-center bangla-font">
              {content.about.visionText}
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8 bangla-font">আমাদের মূল্যবোধ</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-blue-50 rounded-lg">
              <div className="text-4xl mb-4">⭐</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 bangla-font">{content.about.values.excellence}</h3>
              <p className="text-gray-600 bangla-font">সব ক্ষেত্রে উৎকর্ষতা অর্জনের চেষ্টা</p>
            </div>
            <div className="text-center p-6 bg-green-50 rounded-lg">
              <div className="text-4xl mb-4">🤝</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 bangla-font">{content.about.values.integrity}</h3>
              <p className="text-gray-600 bangla-font">সততা ও নৈতিকতার প্রতি অঙ্গীকার</p>
            </div>
            <div className="text-center p-6 bg-purple-50 rounded-lg">
              <div className="text-4xl mb-4">🙏</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 bangla-font">{content.about.values.respect}</h3>
              <p className="text-gray-600 bangla-font">সবার প্রতি সম্মান ও শ্রদ্ধা</p>
            </div>
            <div className="text-center p-6 bg-orange-50 rounded-lg">
              <div className="text-4xl mb-4">💡</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 bangla-font">{content.about.values.innovation}</h3>
              <p className="text-gray-600 bangla-font">নতুনত্ব ও সৃজনশীলতার চর্চা</p>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8 bangla-font">আমাদের অর্জন</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl mb-4">{stat.icon}</div>
                <div className="text-3xl font-bold text-blue-600 mb-2 bangla-font">{stat.number}</div>
                <div className="text-gray-600 bangla-font">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8 bangla-font">আমাদের যাত্রা</h2>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-1 h-full bg-blue-200"></div>
            {achievements.map((achievement, index) => (
              <div key={index} className={`relative flex items-center mb-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                <div className="flex-1 md:w-1/2">
                  <div className={`bg-white p-6 rounded-lg shadow-md ${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`}>
                    <div className="text-2xl font-bold text-blue-600 mb-2 bangla-font">{achievement.year}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 bangla-font">{achievement.title}</h3>
                    <p className="text-gray-600 bangla-font">{achievement.description}</p>
                  </div>
                </div>
                <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">{index + 1}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Facilities */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8 bangla-font">আমাদের সুবিধা</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {facilities.map((facility, index) => (
              <div key={index} className="p-6 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="text-4xl mb-4 text-center">{facility.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center bangla-font">{facility.title}</h3>
                <p className="text-gray-600 text-center bangla-font">{facility.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Leadership */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8 bangla-font">নেতৃত্ব</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {leadership.map((leader, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg">
                <div className="text-6xl mb-4">{leader.image}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2 bangla-font">{leader.name}</h3>
                <p className="text-blue-600 font-medium mb-2 bangla-font">{leader.position}</p>
                <p className="text-sm text-gray-600 mb-1 bangla-font">অভিজ্ঞতা: {leader.experience}</p>
                <p className="text-sm text-gray-600 bangla-font">{leader.education}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4 bangla-font">আমাদের সাথে যোগ দিন</h2>
          <p className="text-xl mb-6 bangla-font">শিক্ষার আলোয় আলোকিত হন</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors bangla-font">
              ভর্তির তথ্য
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors bangla-font">
              যোগাযোগ করুন
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
